import { logActivity } from '../utils/activityLog';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, ChevronDown, Columns, Download, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';
import { toast } from 'sonner';

const fmtMoney = (n) => `$${(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNum = (n) => (n ?? 0).toLocaleString('en-US');
const pctNum = (v) => Number(String(v ?? '0').replace('%', '')) || 0;

const fmtLocal = (d) => {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
};

const buildPresets = () => {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  const yesterday = new Date(t);
  yesterday.setDate(t.getDate() - 1);
  const dow = t.getDay() === 0 ? 6 : t.getDay() - 1;
  const weekStart = new Date(t);
  weekStart.setDate(t.getDate() - dow);
  const lastWeekEnd = new Date(weekStart);
  lastWeekEnd.setDate(weekStart.getDate() - 1);
  const lastWeekStart = new Date(lastWeekEnd);
  lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
  const monthStart = new Date(t.getFullYear(), t.getMonth(), 1);
  const lastMonthEnd = new Date(t.getFullYear(), t.getMonth(), 0);
  const lastMonthStart = new Date(t.getFullYear(), t.getMonth() - 1, 1);
  const threeMonthsAgo = new Date(t);
  threeMonthsAgo.setMonth(t.getMonth() - 3);
  const sixMonthsAgo = new Date(t);
  sixMonthsAgo.setMonth(t.getMonth() - 6);
  const yearStart = new Date(t.getFullYear(), 0, 1);

  return [
    { label: 'Today', from: fmtLocal(t), to: fmtLocal(t) },
    { label: 'Yesterday', from: fmtLocal(yesterday), to: fmtLocal(yesterday) },
    { label: 'This Week', from: fmtLocal(weekStart), to: fmtLocal(t) },
    { label: 'Last Week', from: fmtLocal(lastWeekStart), to: fmtLocal(lastWeekEnd) },
    { label: 'This Month', from: fmtLocal(monthStart), to: fmtLocal(t) },
    { label: 'Last Month', from: fmtLocal(lastMonthStart), to: fmtLocal(lastMonthEnd) },
    { label: 'Last 3M', from: fmtLocal(threeMonthsAgo), to: fmtLocal(t) },
    { label: 'Last 6M', from: fmtLocal(sixMonthsAgo), to: fmtLocal(t) },
    { label: 'This Year', from: fmtLocal(yearStart), to: fmtLocal(t) },
    { label: 'Custom', from: null, to: null },
  ];
};

const TAB_COLS = {
  affiliates: [
    { key: 'affiliate', label: 'Affiliate', fmt: r => r.affiliate, def: true },
    { key: 'funnel', label: 'Funnel', fmt: r => r.funnel, def: true },
    { key: 'clients', label: 'Clients', fmt: r => fmtNum(r.clients), def: true },
    { key: 'cr', label: 'CR %', fmt: r => r.cr, def: true },
    { key: 'ftd', label: 'FTD', fmt: r => fmtNum(r.ftd), def: true },
    { key: 'ftdAmount', label: 'FTD $', fmt: r => fmtMoney(r.ftdAmount), def: true },
    { key: 'noAnswerRate', label: 'Never Answer %', fmt: r => r.noAnswerRate || '0.0%', def: true },
    { key: 'invalidRate', label: 'Invalid %', fmt: r => r.invalidRate || '0.0%', def: true },
    { key: 'deposits', label: 'Deposits', fmt: r => fmtNum(r.deposits), def: false },
    { key: 'redeposits', label: 'Redeposits', fmt: r => fmtNum(r.redeposits), def: false },
    { key: 'redepositsAmount', label: 'ReDep $', fmt: r => fmtMoney(r.redepositsAmount), def: false },
    { key: 'pv', label: 'PV', fmt: r => fmtMoney(r.pv), def: false },
    { key: 'avgPv', label: 'Avg PV', fmt: r => fmtMoney(r.avgPv), def: false },
  ],
  clients: [
    { key: 'clientId', label: 'ID', fmt: r => `#${r.clientId}`, def: true },
    { key: 'name', label: 'Client', fmt: r => r.name, def: true },
    { key: 'status', label: 'Status', fmt: r => r.status || '-', def: true },
    { key: 'affiliate', label: 'Affiliate', fmt: r => r.affiliate || '-', def: true },
    { key: 'agent', label: 'Agent', fmt: r => r.agent || '-', def: true },
    { key: 'ftd', label: 'FTD', fmt: r => fmtNum(r.ftd), def: true },
    { key: 'ftdAmount', label: 'FTD $', fmt: r => fmtMoney(r.ftdAmount), def: true },
    { key: 'redeposits', label: 'Redeposits', fmt: r => fmtNum(r.redeposits), def: true },
    { key: 'redepositsAmount', label: 'ReDep $', fmt: r => fmtMoney(r.redepositsAmount), def: true },
    { key: 'pv', label: 'PV', fmt: r => fmtMoney(r.pv), def: true },
  ],
  statuses: [
    { key: 'status', label: 'Status', fmt: r => r.status, def: true },
    { key: 'clients', label: 'Clients', fmt: r => fmtNum(r.clients), def: true },
    { key: 'percentOfAll', label: '% of Total', fmt: r => r.percentOfAll, def: true },
    { key: 'delta', label: 'Delta', fmt: r => r.delta, def: true },
  ],
  finance: [
    { key: 'currency', label: 'Currency', fmt: r => r.currency, def: true },
    { key: 'numDeposits', label: '# Deposits', fmt: r => fmtNum(r.numDeposits), def: true },
    { key: 'deposits', label: 'Deposits', fmt: r => fmtMoney(r.deposits), def: true },
    { key: 'numWithdrawals', label: '# Withdrawals', fmt: r => fmtNum(r.numWithdrawals), def: false },
    { key: 'withdrawals', label: 'Withdrawals', fmt: r => fmtMoney(r.withdrawals), def: true },
    { key: 'net', label: 'Net', fmt: r => fmtMoney(r.net), def: true },
    { key: 'payments', label: '# Payments', fmt: r => fmtNum(r.payments), def: false },
  ],
  departments: [
    { key: 'groupName', label: 'Name', fmt: r => r.name || '-', def: true },
    { key: 'clients', label: 'Clients', fmt: r => fmtNum(r.clients), def: true },
    { key: 'newClients', label: 'New Clients', fmt: r => fmtNum(r.newClients), def: true },
    { key: 'activeClients', label: 'Active Clients', fmt: r => fmtNum(r.activeClients), def: true },
    { key: 'paidClients', label: 'Paid Clients', fmt: r => fmtNum(r.paidClients), def: true },
    { key: 'cr', label: 'CR %', fmt: r => r.cr, def: true },
    { key: 'ftd', label: 'FTD', fmt: r => fmtNum(r.ftd), def: true },
    { key: 'ftdAmount', label: 'FTD $', fmt: r => fmtMoney(r.ftdAmount), def: true },
    { key: 'redeposits', label: 'Redeposits', fmt: r => fmtNum(r.redeposits), def: true },
    { key: 'redepositsAmount', label: 'ReDep $', fmt: r => fmtMoney(r.redepositsAmount), def: true },
    { key: 'pv', label: 'Amount', fmt: r => fmtMoney(r.pv), def: true },
    { key: 'avgPv', label: 'Avg PV', fmt: r => fmtMoney(r.avgPv), def: false },
  ],
};

const TABS = [
  { key: 'affiliates', label: 'Affiliates' },
  { key: 'clients', label: 'Clients' },
  { key: 'statuses', label: 'Statuses' },
  { key: 'finance', label: 'Finance' },
  { key: 'departments', label: 'Departments' },
];

const DEPARTMENT_BREAKDOWN_SCOPES = [
  { key: 'sales', label: 'Sales' },
  { key: 'retention', label: 'Retention' },
];
const DEPARTMENT_BREAKDOWN_LEVELS = [
  { key: 'desk', labelKey: 'desks' },
  { key: 'team', labelKey: 'teams' },
  { key: 'agent', labelKey: 'agents' },
];
const DEPARTMENT_COLUMN_KEYS = {
  sales: new Set(['groupName', 'clients', 'newClients', 'activeClients', 'paidClients', 'cr', 'ftd', 'ftdAmount', 'pv', 'avgPv']),
  retention: new Set(['groupName', 'clients', 'activeClients', 'paidClients', 'deposits', 'redeposits', 'redepositsAmount', 'pv', 'avgPv']),
};

const DEPARTMENT_SCOPES = [
  { key: 'sales', label: 'Sales' },
  { key: 'retention', label: 'Retention' },
  { key: 'all', label: 'All' },
];
const DepartmentBreakdownView = ({
  rows,
  visibleCols,
  colLabel,
  cls,
  isDark,
  levelLabel,
  department,
}) => {
  const isRetention = department === 'retention';
  const orderedRows = useMemo(
    () => [...rows].sort((a, b) => {
      const primary = isRetention ? (b.pv || 0) - (a.pv || 0) : pctNum(b.cr) - pctNum(a.cr);
      return primary || (b.clients || 0) - (a.clients || 0) || String(a.name || '').localeCompare(String(b.name || ''));
    }),
    [rows, isRetention]
  );
  const totals = orderedRows.reduce((acc, row) => ({
    groups: acc.groups + 1,
    clients: acc.clients + (row.clients || 0),
    newClients: acc.newClients + (row.newClients || 0),
    activeClients: acc.activeClients + (row.activeClients || 0),
    paidClients: acc.paidClients + (row.paidClients || 0),
    deposits: acc.deposits + (row.deposits || 0),
    ftd: acc.ftd + (row.ftd || 0),
    ftdAmount: acc.ftdAmount + (row.ftdAmount || 0),
    redeposits: acc.redeposits + (row.redeposits || 0),
    redepositsAmount: acc.redepositsAmount + (row.redepositsAmount || 0),
    pv: acc.pv + (row.pv || 0),
  }), {
    groups: 0,
    clients: 0,
    newClients: 0,
    activeClients: 0,
    paidClients: 0,
    deposits: 0,
    ftd: 0,
    ftdAmount: 0,
    redeposits: 0,
    redepositsAmount: 0,
    pv: 0,
  });
  const totalAvgPv = totals.paidClients ? totals.pv / totals.paidClients : 0;
  if (orderedRows.length === 0) {
    return (
      <div className={`rounded-lg border py-14 text-center text-sm ${isDark ? 'border-gray-800 bg-[#0a0e1a] text-gray-500' : 'border-gray-200 bg-gray-50 text-gray-400'}`}>
        No department data
      </div>
    );
  }

  return (
    <div className={`rounded-lg border ${isDark ? 'border-gray-800 bg-[#0a0e1a]' : 'border-gray-200 bg-white'}`}>
      <div className={`flex flex-col gap-1 border-b px-4 py-3 sm:flex-row sm:items-end sm:justify-between ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <div>
          <div className={`text-xs font-semibold uppercase ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{levelLabel}</div>
          <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Selected metrics table</div>
        </div>
        <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
          {fmtNum(totals.groups)} groups | {fmtNum(totals.clients)} clients | {fmtNum(totals.paidClients)} paid
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[860px]">
          <thead>
            <tr className={`border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
              {visibleCols.map(c => <th key={c.key} className={cls.th}>{colLabel(c)}</th>)}
            </tr>
          </thead>
          <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
            {orderedRows.map(row => (
              <tr key={row.name} className={isDark ? 'hover:bg-[#111827]' : 'hover:bg-gray-50'}>
                {visibleCols.map(c => (
                  <td
                    key={c.key}
                    className={`${cls.td} ${
                      c.key === 'groupName' ? 'font-semibold' :
                      c.key === 'pv' ? 'text-green-400' :
                      c.key === 'ftdAmount' ? 'text-purple-400' :
                      c.key === 'redepositsAmount' ? 'text-amber-400' :
                      c.key === 'cr' && pctNum(row.cr) > 0 ? 'text-purple-400 font-medium' : ''
                    }`}
                  >
                    {c.fmt(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className={`border-t ${isDark ? 'border-gray-800 bg-[#111827]' : 'border-gray-200 bg-gray-50'}`}>
              {visibleCols.map(c => {
                const totalValue = {
                  groupName: 'Total',
                  clients: fmtNum(totals.clients),
                  newClients: fmtNum(totals.newClients || 0),
                  activeClients: fmtNum(totals.activeClients),
                  paidClients: fmtNum(totals.paidClients),
                  deposits: fmtNum(totals.deposits),
                  ftd: fmtNum(totals.ftd),
                  ftdAmount: fmtMoney(totals.ftdAmount),
                  redeposits: fmtNum(totals.redeposits),
                  redepositsAmount: fmtMoney(totals.redepositsAmount),
                  pv: fmtMoney(totals.pv),
                  avgPv: fmtMoney(totalAvgPv),
                }[c.key] || '';
                return <td key={c.key} className={`${cls.td} font-semibold`}>{totalValue}</td>;
              })}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );

};

export default function Analytics() {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const isDark = theme === 'dark';
  const [tab, setTab] = useState('affiliates');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showColPicker, setShowColPicker] = useState(false);
  const colPickerRef = useRef(null);

  const PRESETS = useMemo(() => buildPresets(), []);
  const initPreset = PRESETS.find(p => p.label === 'This Month');
  const [preset, setPreset] = useState('This Month');
  const [dateFrom, setDateFrom] = useState(initPreset.from);
  const [dateTo, setDateTo] = useState(initPreset.to);
  const [departmentScope, setDepartmentScope] = useState('all');
  const [departmentBreakdownScope, setDepartmentBreakdownScope] = useState('sales');
  const [departmentBreakdownLevel, setDepartmentBreakdownLevel] = useState('desk');

  const [colVis, setColVis] = useState(() => {
    const out = {};
    Object.keys(TAB_COLS).forEach(t => {
      out[t] = {};
      TAB_COLS[t].forEach(c => { out[t][c.key] = c.def; });
    });
    return out;
  });
  const [affiliatesData, setAffiliatesData] = useState([]);
  const [clientsData, setClientsData] = useState(null);
  const [clientDetails, setClientDetails] = useState([]);
  const [clientDetailsError, setClientDetailsError] = useState('');
  const [statusesData, setStatusesData] = useState([]);
  const [financeData, setFinanceData] = useState(null);
  const [departmentRows, setDepartmentRows] = useState([]);

  useEffect(() => {
    const h = (e) => {
      if (colPickerRef.current && !colPickerRef.current.contains(e.target)) setShowColPicker(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => { logActivity('view', 'analytics', null, 'Viewed Analytics'); }, []);
  const load = async () => {
    if (!dateFrom || !dateTo) return;
    if (dateFrom > dateTo) {
      setError(t('analytics.startBeforeEnd'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = {
        date_from: new Date(`${dateFrom}T00:00:00`).toISOString(),
        date_to: new Date(`${dateTo}T23:59:59`).toISOString(),
        department: departmentScope,
      };
      const departmentParams = { ...params, department: departmentBreakdownScope, dimension: departmentBreakdownLevel };
      const [affiliatesRes, clientsRes, statusesRes, financeRes, departmentsRes] = await Promise.allSettled([
        api.get('/analytics/affiliates', { params }),
        api.get('/analytics/clients', { params }),
        api.get('/analytics/statuses', { params }),
        api.get('/analytics/finance', { params }),
        api.get('/analytics/departments', { params: departmentParams }),
      ]);

      if (affiliatesRes.status === 'fulfilled') setAffiliatesData(affiliatesRes.value.data);
      if (clientsRes.status === 'fulfilled') setClientsData(clientsRes.value.data);
      if (statusesRes.status === 'fulfilled') setStatusesData(statusesRes.value.data);
      if (financeRes.status === 'fulfilled') setFinanceData(financeRes.value.data);
      if (departmentsRes.status === 'fulfilled') setDepartmentRows(departmentsRes.value.data);

      const activeRes = { affiliates: affiliatesRes, clients: clientsRes, statuses: statusesRes, finance: financeRes, departments: departmentsRes }[tab];
      if (activeRes?.status === 'rejected') {
        throw activeRes.reason;
      }

      if (tab === 'clients') {
        try {
          const { data: details } = await api.get('/analytics/client-details', { params });
          setClientDetails(details);
          setClientDetailsError('');
        } catch (detailsError) {
          setClientDetails([]);
          setClientDetailsError(detailsError.response?.data?.detail || t('analytics.clientDetailsUnavailable'));
        }
      }
    } catch (e) {
      const message = e.response?.data?.detail || t('analytics.failedLoad');
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tab, dateFrom, dateTo, departmentScope, departmentBreakdownScope, departmentBreakdownLevel]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyPreset = (p) => {
    setPreset(p.label);
    if (p.from) {
      setDateFrom(p.from);
      setDateTo(p.to);
    }
  };

  const availableCols = (t) => {
    const cols = TAB_COLS[t] || [];
    if (t === 'affiliates' && departmentScope === 'sales') {
      const allowed = new Set(['affiliate', 'funnel', 'clients', 'cr', 'ftd', 'ftdAmount', 'noAnswerRate', 'invalidRate']);
      return cols.filter(c => allowed.has(c.key));
    }
    if (t === 'affiliates' && departmentScope === 'retention') {
      const allowed = new Set(['affiliate', 'clients', 'deposits', 'redeposits', 'redepositsAmount', 'pv', 'avgPv']);
      return cols.filter(c => allowed.has(c.key));
    }
    if (t === 'clients' && departmentScope === 'sales') {
      const allowed = new Set(['clientId', 'name', 'status', 'affiliate', 'agent', 'ftd', 'ftdAmount', 'pv']);
      return cols.filter(c => allowed.has(c.key));
    }
    if (t === 'clients' && departmentScope === 'retention') {
      const allowed = new Set(['clientId', 'name', 'status', 'agent', 'redeposits', 'redepositsAmount', 'pv']);
      return cols.filter(c => allowed.has(c.key));
    }
    if (t !== 'departments') return cols;
    const allowed = DEPARTMENT_COLUMN_KEYS[departmentBreakdownScope] || DEPARTMENT_COLUMN_KEYS.sales;
    return cols.filter(c => allowed.has(c.key));
  };
  const visibleCols = (t) => availableCols(t).filter(c => colVis[t]?.[c.key] !== false);
  const presetLabel = (label) => t(`analytics.presets.${label.replace(/\s+/g, '').replace(/[^A-Za-z0-9]/g, '')}`);
  const tabLabel = (key) => t(`analytics.tabs.${key}`);
  const tr = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };
  const colLabel = (col) => tr(`analytics.columns.${col.key}`, col.label);
  const scopeLabel = (key) => key === 'sales' ? 'Sales' : key === 'retention' ? 'Retention' : t('analytics.all');
  const breakdownLevelLabel = (key) => t(`analytics.departmentLevels.${DEPARTMENT_BREAKDOWN_LEVELS.find(level => level.key === key)?.labelKey || key}`);
  const rowsForTab = () => {
    if (tab === 'affiliates') return affiliatesData;
    if (tab === 'clients') return clientDetails;
    if (tab === 'statuses') return statusesData;
    if (tab === 'finance') return financeData?.rows || [];
    if (tab === 'departments') return departmentRows;
    return [];
  };

  const exportCsv = () => {
    const cols = visibleCols(tab);
    if (tab !== 'clients' && !cols.length) return;
    let rows = rowsForTab();
    if (tab === 'clients' && clientDetails.length === 0) {
      rows = [
        { label: t('analytics.kpi.clients'), value: clientsData?.allClients ?? 0 },
        { label: t('analytics.kpi.newClients'), value: clientsData?.newClients ?? 0 },
        { label: t('analytics.kpi.activeClients'), value: clientsData?.activeClients ?? 0 },
        { label: t('analytics.depositors'), value: clientsData?.depositors ?? 0 },
        { label: 'FTD', value: clientsData?.ftd ?? 0 },
        { label: 'FTD $', value: clientsData?.ftdAmount ?? 0 },
        { label: t('analytics.redeposits'), value: clientsData?.redepositors ?? 0 },
        { label: 'ReDep $', value: clientsData?.redepositsAmount ?? 0 },
        { label: t('analytics.kpi.redepositRate'), value: clientsData?.redepositionsPercent ?? '0%' },
        { label: 'PV', value: clientsData?.pv ?? 0 },
      ];
    }
    const csvCols = tab === 'clients' && clientDetails.length === 0 ? [{ key: 'label', label: t('analytics.metric'), fmt: r => r.label }, { key: 'value', label: t('analytics.value'), fmt: r => r.value }] : cols.map(c => ({ ...c, label: colLabel(c) }));
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [csvCols.map(c => esc(c.label)).join(','), ...rows.map(r => csvCols.map(c => esc(c.fmt(r))).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const scopeName = tab === 'departments' ? `${departmentBreakdownScope}-${departmentBreakdownLevel}` : departmentScope;
    a.download = `analytics-${scopeName}-${tab}-${dateFrom}-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const cls = {
    card: `rounded-lg border ${isDark ? 'bg-[#0f1729] border-gray-800' : 'bg-white border-gray-200'}`,
    th: `px-4 py-3 text-left text-xs font-medium uppercase ${isDark ? 'text-gray-400 bg-[#0a0e1a]' : 'text-gray-500 bg-gray-50'}`,
    td: `px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`,
    tabBtn: (active) => `px-5 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
      active ? 'border-purple-500 text-purple-400' : `border-transparent ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`
    }`,
    btn: `px-3 py-1.5 text-sm border rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDark ? 'border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`,
    presetBtn: (active) => `px-3 py-1.5 text-xs rounded-md font-medium transition-colors whitespace-nowrap ${
      active ? 'bg-purple-600 text-white' : isDark ? 'bg-[#1a1f3a] text-gray-400 border border-gray-700 hover:text-gray-200 hover:bg-[#2a2f4a]' : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
    }`,
    input: `h-9 px-3 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`,
    label: `text-xs font-medium ${isDark ? 'text-gray-500' : 'text-gray-500'}`,
  };

  return (
    <div className="p-6 max-w-[1600px]">
      <div className={`${cls.card} overflow-visible`}>
        <div className={`border-b px-5 py-4 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <div className={`text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>Analytics</div>
              <div className={`mt-1 text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                {`Dashboard | ${dateFrom} ${t('analytics.to')} ${dateTo}`}
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[180px]">
                <label className={cls.label} htmlFor="analytics-period">{t('analytics.period')}</label>
                <div className="relative mt-1">
                  <select
                    id="analytics-period"
                    value={preset}
                    onChange={e => {
                      const next = PRESETS.find(p => p.label === e.target.value);
                      if (next) applyPreset(next);
                    }}
                    className={`${cls.input} w-full appearance-none pr-9`}
                  >
                    {PRESETS.map(p => <option key={p.label} value={p.label}>{presetLabel(p.label)}</option>)}
                  </select>
                  <ChevronDown size={15} className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                </div>
              </div>

              <div className={`inline-flex h-9 items-center gap-2 rounded-md border px-3 text-xs ${isDark ? 'border-gray-800 bg-[#0a0e1a] text-gray-400' : 'border-gray-200 bg-gray-50 text-gray-500'}`}>
                <CalendarDays size={14} />
                <span className="font-medium tabular-nums">{dateFrom}</span>
                <span className={isDark ? 'text-gray-600' : 'text-gray-400'}>{t('analytics.to')}</span>
                <span className="font-medium tabular-nums">{dateTo}</span>
              </div>

              {preset === 'Custom' && (
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <label className={cls.label} htmlFor="analytics-date-from">{t('analytics.fromDate')}</label>
                    <input id="analytics-date-from" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className={`${cls.input} mt-1`} />
                  </div>
                  <div>
                    <label className={cls.label} htmlFor="analytics-date-to">{t('analytics.toDate')}</label>
                    <input id="analytics-date-to" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className={`${cls.input} mt-1`} />
                  </div>
                </div>
              )}

              {tab !== 'departments' && (
                <div className={`inline-flex h-9 rounded-md border p-1 ${isDark ? 'border-gray-800 bg-[#0a0e1a]' : 'border-gray-200 bg-gray-50'}`}>
                  {DEPARTMENT_SCOPES.map(scope => (
                    <button
                      key={scope.key}
                      type="button"
                      onClick={() => setDepartmentScope(scope.key)}
                      className={`rounded px-3 text-xs font-semibold transition-colors ${
                        departmentScope === scope.key
                          ? 'bg-purple-600 text-white shadow-sm'
                          : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-800'
                      }`}
                    >
                      {scopeLabel(scope.key)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <>
        <div className={`flex flex-col gap-3 border-b px-5 py-3 lg:flex-row lg:items-center lg:justify-between ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          <div className="flex overflow-x-auto">
            {TABS.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} className={cls.tabBtn(tab === t.key)}>{tabLabel(t.key)}</button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {TAB_COLS[tab] && (
              <div className="relative" ref={colPickerRef}>
                <button onClick={() => setShowColPicker(v => !v)} className={`flex items-center gap-1.5 ${cls.btn}`} title={t('analytics.chooseVisibleColumns')}>
                  <Columns size={14} /> {t('analytics.metrics')} <ChevronDown size={12} />
                </button>
                {showColPicker && (
                  <div className={`absolute right-0 top-full mt-1 z-50 rounded-lg shadow-xl border p-3 space-y-2 min-w-[180px] ${isDark ? 'bg-[#1a1f3a] border-gray-700' : 'bg-white border-gray-200'}`}>
                    <div className={`text-xs font-semibold mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t('analytics.visibleColumns')}</div>
                    {availableCols(tab).map(c => (
                      <label key={c.key} className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={colVis[tab]?.[c.key] !== false}
                          onChange={e => setColVis(v => ({ ...v, [tab]: { ...v[tab], [c.key]: e.target.checked } }))}
                          className="w-3.5 h-3.5 rounded accent-purple-500"
                        />
                        <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{colLabel(c)}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
            <button onClick={exportCsv} className={`flex items-center gap-1.5 ${cls.btn}`} disabled={loading || !!error || rowsForTab().length === 0}>
              <Download size={14} /> {t('analytics.export')}
            </button>
            <button onClick={load} className={cls.btn} title={t('analytics.refresh')} disabled={loading}>
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {tab === 'departments' && (
          <div className={`flex flex-col gap-3 border-b px-5 py-3 md:flex-row md:items-center md:justify-between ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
            <div className={`inline-flex h-9 w-fit rounded-md border p-1 ${isDark ? 'border-gray-800 bg-[#0a0e1a]' : 'border-gray-200 bg-gray-50'}`}>
              {DEPARTMENT_BREAKDOWN_SCOPES.map(scope => (
                <button
                  key={scope.key}
                  type="button"
                  onClick={() => setDepartmentBreakdownScope(scope.key)}
                  className={`rounded px-3 text-xs font-semibold transition-colors ${
                    departmentBreakdownScope === scope.key
                      ? 'bg-purple-600 text-white shadow-sm'
                      : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {scope.label}
                </button>
              ))}
            </div>
            <div className={`inline-flex h-9 w-fit rounded-md border p-1 ${isDark ? 'border-gray-800 bg-[#0a0e1a]' : 'border-gray-200 bg-gray-50'}`}>
              {DEPARTMENT_BREAKDOWN_LEVELS.map(level => (
                <button
                  key={level.key}
                  type="button"
                  onClick={() => setDepartmentBreakdownLevel(level.key)}
                  className={`rounded px-3 text-xs font-semibold transition-colors ${
                    departmentBreakdownLevel === level.key
                      ? 'bg-purple-600 text-white shadow-sm'
                      : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {breakdownLevelLabel(level.key)}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="p-5 space-y-5">
          {error ? (
            <div className={`text-center py-12 text-sm ${isDark ? 'text-red-300' : 'text-red-600'}`}>{error}</div>
          ) : loading ? (
            <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{t('analytics.loading')}</div>
          ) : tab === 'departments' ? (
            <DepartmentBreakdownView
              rows={departmentRows}
              visibleCols={visibleCols('departments')}
              colLabel={colLabel}
              cls={cls}
              isDark={isDark}
              levelLabel={breakdownLevelLabel(departmentBreakdownLevel)}
              department={departmentBreakdownScope}
            />
          ) : tab === 'clients' ? (
            clientsData ? (
              <div className="space-y-3">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[920px]">
                    <thead>
                      <tr className={`border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                        {visibleCols('clients').map(c => <th key={c.key} className={cls.th}>{colLabel(c)}</th>)}
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
                      {clientDetailsError ? (
                        <tr>
                          <td colSpan={visibleCols('clients').length} className={`${cls.td} text-center py-10 ${isDark ? 'text-amber-300' : 'text-amber-600'}`}>{clientDetailsError}</td>
                        </tr>
                      ) : clientDetails.length === 0 ? (
                        <tr>
                          <td colSpan={visibleCols('clients').length} className={`${cls.td} text-center py-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{t('analytics.noClientDetails')}</td>
                        </tr>
                      ) : clientDetails.map((r) => (
                        <tr
                          key={r.clientId}
                          onClick={() => navigate(`/clients/${r.clientId}`)}
                          className={`cursor-pointer ${isDark ? 'hover:bg-[#1a1f3a]' : 'hover:bg-gray-50'}`}
                        >
                          {visibleCols('clients').map(c => (
                            <td
                              key={c.key}
                              className={`${cls.td} ${
                                c.key === 'name' ? 'font-medium' :
                                c.key === 'ftd' && r.ftd > 0 ? 'text-purple-400 font-medium' :
                                c.key === 'ftdAmount' && r.ftdAmount > 0 ? 'text-purple-400' :
                                c.key === 'redeposits' && r.redeposits > 0 ? 'text-amber-400 font-medium' :
                                c.key === 'redepositsAmount' && r.redepositsAmount > 0 ? 'text-amber-400' :
                                c.key === 'pv' && r.pv > 0 ? 'text-green-400' : ''
                              }`}
                            >
                              {c.fmt(r)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className={`pt-2 text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                    {t('analytics.clientDetailsHint')}
                  </div>
                </div>
              </div>
            ) : (
              <div className={`text-center py-10 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{t('analytics.noData')}</div>
            )
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[540px]">
                <thead>
                  <tr className={`border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                    {visibleCols(tab).map(c => <th key={c.key} className={cls.th}>{colLabel(c)}</th>)}
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
                  {rowsForTab().length === 0 ? (
                    <tr>
                      <td colSpan={visibleCols(tab).length} className={`${cls.td} text-center py-10 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{t('analytics.noDataForPeriod')}</td>
                    </tr>
                  ) : rowsForTab().map((r, i) => (
                    <tr key={i} className={isDark ? 'hover:bg-[#1a1f3a]' : 'hover:bg-gray-50'}>
                      {visibleCols(tab).map(c => (
                        <td
                          key={c.key}
                          className={`${cls.td} ${
                            c.key === 'delta'
                              ? String(r.delta).startsWith('+') ? 'text-green-400 font-medium' : String(r.delta).startsWith('-') ? 'text-red-400 font-medium' : ''
                              : c.key === 'deposits' || c.key === 'pv' ? 'text-green-400'
                              : c.key === 'ftdAmount' ? 'text-purple-400'
                              : c.key === 'redepositsAmount' ? 'text-amber-400'
                              : c.key === 'withdrawals' ? 'text-red-400'
                              : c.key === 'net' ? ((r.net || 0) >= 0 ? 'text-green-400' : 'text-red-400')
                              : c.key === 'cr' && pctNum(r.cr) > 0 ? 'text-purple-400 font-medium'
                              : ''
                          }`}
                        >
                          {c.fmt(r)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
      </div>
    </div>
  );
}
